import { AppState, AppAction, DataScope } from '../types';

const getInitialHouseholdId = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('currentHouseholdId') || null;
  }
  return null;
};

const getInitialDataScope = (): DataScope => {
  if (typeof window !== 'undefined') {
    return (localStorage.getItem('activeDataScope') as DataScope) || 'PERSONAL';
  }
  return 'PERSONAL';
};

export const initialState: AppState = {
  currentUser: null,
  currentHouseholdId: getInitialHouseholdId(),
  activeDataScope: getInitialDataScope(),
  households: [],
  tasks: [],
  userProfiles: {},
  inventory: [],
  personalEvents: [],
  householdEvents: [],
  customEvents: [],
  maintenanceTasks: [],
  notifications: [],
  theme: 'dark',
  isBooting: true,
  contacts: [],
  dailySpark: null,
  lastSyncTimestamp: null,
  intelligence: { sparks: [] },
  auditLogs: [],
  dashboardLayout: [],
  customGroceryCategories: { personal: {}, household: {} },

  // Legacy add-on optional fields
  calendarEvents: [],
  calendarSeries: [],
  householdSeries: [],
  personalSeries: [],
  vehicles: [],
  vehicleBookings: [],
  pets: [],
  petCareTasks: [],
  maintenanceSchedules: [],
  maintenanceLogs: [],
};

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, currentUser: action.payload };
    case 'UPDATE_USER':
      return {
        ...state,
        currentUser: state.currentUser ? { ...state.currentUser, ...action.payload } : null,
      };
    case 'SET_BOOTING':
      return { ...state, isBooting: action.payload };
    case 'SET_HOUSEHOLDS':
      return { ...state, households: action.payload };
    case 'SET_CURRENT_HOUSEHOLD':
      if (typeof window !== 'undefined') {
        if (action.payload) localStorage.setItem('currentHouseholdId', action.payload);
        else localStorage.removeItem('currentHouseholdId');
      }
      return { ...state, currentHouseholdId: action.payload };
    case 'ADD_HOUSEHOLD':
      return { ...state, households: [...state.households, action.payload] };
    case 'UPDATE_HOUSEHOLD':
      return {
        ...state,
        households: state.households.map(h =>
          h.id === action.payload.id ? { ...h, ...action.payload.updates } : h
        ),
      };
    case 'REMOVE_HOUSEHOLD':
      return { ...state, households: state.households.filter(h => h.id !== action.payload) };
    case 'SET_DATA_SCOPE':
      if (typeof window !== 'undefined') localStorage.setItem('activeDataScope', action.payload);
      return { ...state, activeDataScope: action.payload };
    case 'SET_TASKS':
      return { ...state, tasks: action.payload };
    case 'ADD_TASK':
      return { ...state, tasks: [...state.tasks, action.payload] };
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map(t => t.id === action.payload.id ? { ...t, ...action.payload.updates } : t),
      };
    case 'REMOVE_TASK':
      return { ...state, tasks: state.tasks.filter(t => t.id !== action.payload) };
    case 'SET_INVENTORY':
      return { ...state, inventory: action.payload };
    case 'ADD_INVENTORY_ITEM':
      return { ...state, inventory: [...state.inventory, action.payload] };
    case 'SET_PERSONAL_EVENTS':
      return { ...state, personalEvents: action.payload };
    case 'ADD_PERSONAL_EVENT':
      return { ...state, personalEvents: [...state.personalEvents, action.payload] };
    case 'UPDATE_PERSONAL_EVENT':
      return {
        ...state,
        personalEvents: state.personalEvents.map(e => e.id === action.payload.id ? action.payload : e),
      };
    case 'DELETE_PERSONAL_EVENT':
      return { ...state, personalEvents: state.personalEvents.filter(e => e.id !== action.payload) };
    case 'SET_HOUSEHOLD_EVENTS':
      return { ...state, householdEvents: action.payload };
    case 'ADD_HOUSEHOLD_EVENT':
      return { ...state, householdEvents: [...state.householdEvents, action.payload] };
    case 'UPDATE_HOUSEHOLD_EVENT':
      return {
        ...state,
        householdEvents: state.householdEvents.map(e => e.id === action.payload.id ? action.payload : e),
      };
    case 'DELETE_HOUSEHOLD_EVENT':
      return { ...state, householdEvents: state.householdEvents.filter(e => e.id !== action.payload) };
    case 'SET_MAINTENANCE_TASKS':
      return { ...state, maintenanceTasks: action.payload };
    case 'SET_NOTIFICATIONS':
      return { ...state, notifications: action.payload };
    case 'DELETE_NOTIFICATION':
      return { ...state, notifications: state.notifications.filter(n => n.id !== action.payload) };
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => n.id === action.payload ? { ...n, read: true } : n),
      };
    case 'CLEAR_ALL_NOTIFICATIONS':
      return { ...state, notifications: [] };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'TOGGLE_THEME':
      return { ...state, theme: state.theme === 'light' ? 'dark' : 'light' };
    case 'SET_SYNC':
      return { ...state, lastSyncTimestamp: action.payload };
    case 'UPDATE_LAYOUT':
      return { ...state, dashboardLayout: action.payload };
    case 'SET_CONTACTS':
      return { ...state, contacts: action.payload };
    case 'ADD_CONTACT':
      return { ...state, contacts: [...state.contacts, action.payload] };
    case 'REMOVE_CONTACT':
      return { ...state, contacts: state.contacts.filter(c => c.id !== action.payload) };
    case 'SET_AUDIT_LOGS':
      return { ...state, auditLogs: action.payload };
    case 'SAVE_CUSTOM_GROCERY_CATEGORIES':
    case 'SET_CUSTOM_GROCERY_CATEGORIES': {
      const { scope, categories } = action.payload as any;
      return {
        ...state,
        customGroceryCategories: {
          ...state.customGroceryCategories,
          [scope === 'household' ? 'household' : 'personal']: categories,
        },
      };
    }
    case 'UPDATE_HOUSEHOLD_MEMBER':
      return {
        ...state,
        households: state.households.map(h => {
          if (h.id !== action.payload.householdId) return h;
          return {
            ...h,
            members: h.members.map(m =>
              m.userId === action.payload.userId ? { ...m, ...action.payload.updates } : m
            ),
          };
        }),
      };
    case 'UPDATE_MEMBER_PERMISSIONS':
      return {
        ...state,
        households: state.households.map(h => {
          if (h.id !== action.payload.householdId) return h;
          return {
            ...h,
            permissions: { ...h.permissions, [action.payload.role]: action.payload.permissions },
          };
        }),
      };
    case 'SET_VEHICLES':
      return { ...state, vehicles: action.payload };
    case 'ADD_VEHICLE':
      return { ...state, vehicles: [...(state.vehicles || []), action.payload] };
    case 'UPDATE_VEHICLE':
      return {
        ...state,
        vehicles: (state.vehicles || []).map(v =>
          v.id === action.payload.id ? { ...v, ...action.payload.updates } : v
        ),
      };
    case 'SET_PETS':
      return { ...state, pets: action.payload };
    case 'ADD_PET':
      return { ...state, pets: [...(state.pets || []), action.payload] };
    case 'UPDATE_PET':
      return {
        ...state,
        pets: (state.pets || []).map(p =>
          p.id === action.payload.id ? { ...p, ...action.payload.updates } : p
        ),
      };
    case 'SET_MAINTENANCE_LOGS':
      return { ...state, maintenanceLogs: action.payload };
    case 'ADD_MAINTENANCE_LOG':
      return { ...state, maintenanceLogs: [...(state.maintenanceLogs || []), action.payload] };
    case 'SET_EVENTS':
      return { ...state, calendarEvents: action.payload };
    case 'SET_VEHICLE_BOOKINGS':
      return { ...state, vehicleBookings: action.payload };
    default:
      return state;
  }
}
